import React, { useEffect, useRef, useState } from 'react';
import {
    Alert,
    Box,
    Button,
    Stack,
    Typography,
    TableContainer,
    Table,
    TableHead,
    TableBody,
    TableRow,
    TableCell,
    Paper,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogContentText,
    DialogActions,
} from '@mui/material';
import { conferirCaixaSis, consolidarCaixaSis } from '../../../api/caixaSis';

const labels = {
    ABERTO: 'Aberto no SIS',
    FECHADO: 'Fechado — aguardando conferência',
    CONFERIDO: 'Conferido — aguardando consolidação',
    CONSOLIDADO: 'Consolidado',
};
const dinheiro = (v) =>
    Number.isFinite(Number(v)) ? Number(v).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' }) : '—';
const dataHora = (v) => (v ? new Date(v).toLocaleString('pt-BR') : '—');
export default function CaixaSisPanel({ data, atualizar, voltar, erroConsulta, carregando }) {
    const [ocupado, setOcupado] = useState(false);
    const [acao, setAcao] = useState(null);
    const [mensagem, setMensagem] = useState(null);
    const trava = useRef(false);
    const vivo = useRef(true);
    useEffect(() => {
        vivo.current = true;
        return () => {
            vivo.current = false;
        };
    }, []);
    async function executar() {
        if (trava.current || !acao || erroConsulta || carregando) return;
        trava.current = true;
        setOcupado(true);
        let resultado;
        try {
            await (acao === 'conferir' ? conferirCaixaSis : consolidarCaixaSis)(data.caixaUuid);
            resultado = {
                severity: 'success',
                text: acao === 'conferir' ? 'Conferência registrada.' : 'Caixa consolidado. Estoque e financeiro processados.',
            };
        } catch (e) {
            resultado = { severity: 'error', text: `${e.message} Consulte o estado atualizado antes de repetir a ação.` };
        }
        try {
            await atualizar();
        } catch (e) {
            resultado = { severity: 'warning', text: `${resultado.text} Não foi possível atualizar o estado: ${e.message}` };
        } finally {
            trava.current = false;
            if (vivo.current) {
                setOcupado(false);
                setAcao(null);
                setMensagem(resultado);
            }
        }
    }
    const resumo = data.resumo || {};
    const totais = [
        ['Vendas', resumo.vendas],
        ['Recebimentos avulsos', resumo.recebimentosAvulsos],
        ['Pagamentos avulsos', resumo.pagamentosAvulsos],
        ['Sangrias', resumo.sangrias],
        ['Saldo da conta do caixa', resumo.saldoContaCaixa],
    ];
    return (
        <Box sx={{ width: '100%', py: 2 }} aria-busy={ocupado}>
            <Typography variant="h5">Caixa SIS {data.contador ? `nº ${data.contador}` : ''}</Typography>
            <Typography sx={{ overflowWrap: 'anywhere' }}>Sessão: {data.caixaExternoId}</Typography>
            <Typography sx={{ my: 1 }}>{labels[data.estado] || data.estado}</Typography>
            <Typography variant="body2">
                Abertura: {dataHora(data.abertura)} · Fechamento: {dataHora(data.fechamento)} · Conferência:{' '}
                {dataHora(data.conferidoEm)}
            </Typography>
            <Alert severity="info" sx={{ my: 2 }}>
                Vendas e movimentações são registradas no SIS. Aqui você consulta, confere e consolida o caixa. A consolidação
                processa estoque e financeiro.
            </Alert>
            {mensagem && (
                <Alert severity={mensagem.severity} sx={{ mb: 2 }}>
                    {mensagem.text}
                </Alert>
            )}
            {erroConsulta && (
                <Alert severity="error" sx={{ mb: 2 }}>
                    {erroConsulta}
                </Alert>
            )}
            {data.impedimentos?.map((m, i) => (
                <Alert severity="info" key={i} sx={{ mb: 1 }}>
                    {m}
                </Alert>
            ))}
            {data.pendencias?.length > 0 && (
                <Alert severity="warning">Há pendências no fechamento: {data.pendencias.map((p) => p.codigo).join(', ')}.</Alert>
            )}
            <Stack direction="row" flexWrap="wrap" gap={2} sx={{ my: 2 }}>
                {totais.map(([label, value]) => (
                    <Paper variant="outlined" sx={{ p: 2 }} key={label}>
                        <Typography variant="body2">{label}</Typography>
                        <Typography variant="h6">{dinheiro(value)}</Typography>
                    </Paper>
                ))}
            </Stack>
            <Typography variant="body2" sx={{ mb: 1 }}>
                Pedidos: {data.pedidos?.length || 0} · Eventos: {data.eventos?.length || 0}. A sangria possui saída e entrada em
                contas distintas.
            </Typography>
            <TableContainer component={Paper} variant="outlined" sx={{ maxHeight: 480 }}>
                <Table size="small" stickyHeader aria-label="Lançamentos recebidos do SIS">
                    <TableHead>
                        <TableRow>
                            {['Origem', 'Tipo', 'Pessoa', 'Conta', 'Forma', 'Histórico', 'Valor'].map((t) => (
                                <TableCell key={t}>{t}</TableCell>
                            ))}
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {(data.lancamentos || []).map((l, i) => (
                            <TableRow key={l.uuid || i}>
                                <TableCell>{l.origem}</TableCell>
                                <TableCell>{l.tipo}</TableCell>
                                <TableCell>{l.pessoa}</TableCell>
                                <TableCell>{l.conta}</TableCell>
                                <TableCell>{l.forma}</TableCell>
                                <TableCell>{l.historico}</TableCell>
                                <TableCell>{dinheiro(l.valor)}</TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            </TableContainer>
            <Box sx={{ mt: 2, overflowWrap: 'anywhere' }}>
                <Typography variant="body2">Pedidos SIS: {(data.pedidos || []).join(', ') || 'Nenhum'}</Typography>
                <Typography variant="body2">Eventos SIS: {(data.eventos || []).join(', ') || 'Nenhum'}</Typography>
            </Box>
            <Stack direction="row" flexWrap="wrap" gap={1} sx={{ mt: 2 }}>
                <Button type="button" onClick={voltar} disabled={ocupado}>
                    Voltar
                </Button>
                <Button
                    type="button"
                    disabled={ocupado}
                    onClick={async () => {
                        try {
                            await atualizar();
                            setMensagem(null);
                        } catch (e) {
                            setMensagem({ severity: 'error', text: e.message });
                        }
                    }}
                >
                    Atualizar
                </Button>
                <Button
                    type="button"
                    variant="contained"
                    disabled={ocupado || carregando || Boolean(erroConsulta) || !data.podeConferir}
                    onClick={() => setAcao('conferir')}
                >
                    Conferir
                </Button>
                <Button
                    type="button"
                    variant="contained"
                    color="success"
                    disabled={ocupado || carregando || Boolean(erroConsulta) || !data.podeConsolidar}
                    onClick={() => setAcao('consolidar')}
                >
                    Consolidar estoque e financeiro
                </Button>
            </Stack>
            <Dialog
                open={Boolean(acao)}
                onClose={() => {
                    if (!ocupado) setAcao(null);
                }}
            >
                <DialogTitle>{acao === 'conferir' ? 'Confirmar conferência' : 'Confirmar consolidação'}</DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        {acao === 'conferir'
                            ? 'Confirma que conferiu os lançamentos e o resumo desta sessão SIS?'
                            : 'Esta ação transforma os movimentos e gera/baixa o financeiro. Deseja consolidar este caixa?'}
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button disabled={ocupado} onClick={() => setAcao(null)}>
                        Cancelar
                    </Button>
                    <Button disabled={ocupado || carregando || Boolean(erroConsulta)} onClick={executar}>
                        {ocupado ? 'Processando…' : 'Confirmar'}
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
}
